import { css, html, LitElement, nothing } from "lit";

import { formatDate, formatSize } from "../lib/format";
import {
  artifactActionIntent,
  type ArtifactActionDescriptor,
} from "../lib/artifact-actions";
import { lockActionsFor, lockBadge, lockStateOf, type LockActionId } from "../lib/locking";
import { nextSort, sortRows, type SortDir } from "../lib/sort";
import { controlStyles } from "../styles/tokens";
import type {
  ArtifactActionId,
  ArtifactActionIntent,
  ArtifactListRow,
  ArtifactSearchRow,
} from "../transport/types";

/**
 * <modulina-artifact-table> — the sortable results table (spec-web-ui §2,
 * columns 1:1 su server_files.html + address context columns in the
 * tenant-wide search mode). Client-side sort (lib/sort.ts — pure, tested),
 * sticky header with direction arrows, keyboard-operable rows (tab +
 * Enter/Space opens the versions panel), and per-row action buttons driven
 * by the injected `actions` descriptors (capability data — the browser
 * passes `["download"]`; other shells pass their own set).
 *
 * TAKE-FOR-WORK (spec-artifact-locking §8): the "Būsena" column shows the
 * lock badge for EVERY shell — who has the file and since when is plain row
 * data, useful even where nothing can be taken. The take/return/abandon/
 * takeover BUTTONS are separate and capability-gated (`lockUi`), so a shell
 * whose transport cannot lock never offers them.
 *
 * Props in: rows (already fetched), mode, selectedId, actions, me, lockUi,
 * canReturn. Events out: `open-artifact` (detail: { row }),
 * `artifact-action` (detail: {@link ArtifactActionIntent}, targeting the
 * row's CURRENT version) and `lock-action` (detail: {@link LockActionEvent}).
 * Zero transport knowledge.
 */

type Row = ArtifactListRow | ArtifactSearchRow;

/** What a lock button emits — the parent owns every lock call. */
export interface LockActionEvent {
  action: LockActionId;
  artifactId: string;
}

interface Column {
  key: string;
  label: string;
  numeric?: boolean;
}

const PROJECT_COLUMNS: Column[] = [
  { key: "name", label: "Pavadinimas" },
  // Lock state right after the name: "who has this file" is the first thing
  // a colleague needs before deciding to open or take it (spec §8).
  { key: "lockedBy", label: "Būsena" },
  { key: "type", label: "Tipas" },
  { key: "currentVersionNumber", label: "Versijos", numeric: true },
  { key: "sizeBytes", label: "Dydis", numeric: true },
  { key: "updatedAt", label: "Atnaujinta" },
  { key: "currentVersionAuthor", label: "Autorius" },
  { key: "currentVersionMessage", label: "Žinutė" },
];

/** Tenant-wide hits carry their address so a match is directly actionable. */
const SEARCH_COLUMNS: Column[] = [
  { key: "clientName", label: "Klientas" },
  { key: "projectName", label: "Projektas" },
  ...PROJECT_COLUMNS,
];

const NUMERIC_KEYS: ReadonlySet<string> = new Set(["currentVersionNumber", "sizeBytes"]);

export class ModulinaArtifactTable extends LitElement {
  static override properties = {
    rows: { attribute: false },
    mode: {},
    selectedId: { attribute: false },
    actions: { attribute: false },
    me: { attribute: false },
    lockUi: { attribute: false },
    canReturn: { attribute: false },
    emptyText: {},
    sortKey: { state: true },
    sortDir: { state: true },
  };

  declare rows: Row[];
  /** "project" (list rows) | "search" (rows with address context). */
  declare mode: "project" | "search";
  declare selectedId: string | null;
  /** Per-artifact actions to render as row buttons (capability data). */
  declare actions: ArtifactActionDescriptor[];
  /** The signed-in principal (web session = the user's EMAIL), so a row can
   * say „Paimta jūsų" instead of naming the user to themselves. null = the
   * shell knows no identity → every lock reads as a colleague's (see
   * lockStateOf). */
  declare me: string | null;
  /** Capability data: does this shell's transport support locking at all?
   * False = badges only, no take/return/abandon/takeover buttons. */
  declare lockUi: boolean;
  /** Capability data: is the upload affordance available for these rows?
   * Returning IS uploading, so „Grąžinti" is offered only where an upload
   * target exists (the project view of an upload-capable transport). */
  declare canReturn: boolean;
  declare emptyText: string;
  declare sortKey: string | null;
  declare sortDir: SortDir;

  constructor() {
    super();
    this.rows = [];
    this.mode = "project";
    this.selectedId = null;
    this.actions = [];
    this.me = null;
    this.lockUi = false;
    this.canReturn = false;
    this.emptyText = "Artefaktų nerasta.";
    this.sortKey = null;
    this.sortDir = 1;
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        background: var(--m-color-surface);
        box-shadow: var(--m-shadow);
        overflow: auto;
        max-height: 60vh;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--m-fs-small);
      }
      th,
      td {
        text-align: left;
        padding: var(--m-space-2) var(--m-space-3);
        border-bottom: 1px solid var(--m-color-border);
        white-space: nowrap;
      }
      td.wrap {
        white-space: normal;
        word-break: break-word;
        min-width: 12ch;
      }
      th {
        position: sticky;
        top: 0;
        background: var(--m-color-bg);
        color: var(--m-color-text-muted);
        font-size: var(--m-fs-label);
        user-select: none;
        z-index: 1;
      }
      th button.sort {
        border: none;
        background: none;
        padding: 0;
        font: inherit;
        color: inherit;
        cursor: pointer;
      }
      th button.sort:hover {
        color: var(--m-color-accent);
      }
      .arrow {
        color: var(--m-color-accent);
        font-size: 9px;
      }
      tbody tr {
        cursor: pointer;
      }
      tbody tr:hover {
        background: var(--m-color-accent-soft);
      }
      tbody tr.selected {
        background: var(--m-color-selected);
      }
      td.actions {
        text-align: right;
        white-space: nowrap;
      }
      /* Lock badge (spec §8): "mine" reads calm, a colleague's lock reads
         as a warning — the user must not mistake one for the other at a
         glance. Both keep the holder + age as TEXT (never colour alone). */
      .lock-badge {
        display: inline-block;
        border-radius: var(--m-radius-small);
        padding: 1px var(--m-space-2);
        font-size: var(--m-fs-label);
        white-space: normal;
      }
      .lock-badge.mine {
        background: var(--m-color-ok-soft);
        color: var(--m-color-ok);
      }
      .lock-badge.other {
        background: var(--m-color-danger-soft);
        color: var(--m-color-danger);
      }
      .empty {
        color: var(--m-color-text-muted);
        text-align: center;
        padding: var(--m-space-5);
        cursor: default;
      }
    `,
  ];

  private get columns(): Column[] {
    return this.mode === "search" ? SEARCH_COLUMNS : PROJECT_COLUMNS;
  }

  override render() {
    const columns = this.columns;
    const rows = this.sortKey
      ? sortRows(this.rows, this.sortKey, this.sortDir, NUMERIC_KEYS)
      : this.rows;
    return html`
      <table>
        <thead>
          <tr>
            ${columns.map((col) => this.renderHeader(col))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${rows.length === 0
            ? html`<tr>
                <td class="empty" colspan=${columns.length + 1}>${this.emptyText}</td>
              </tr>`
            : rows.map((row) => this.renderRow(row, columns))}
        </tbody>
      </table>
    `;
  }

  private renderHeader(col: Column) {
    const active = this.sortKey === col.key;
    const dirWord = this.sortDir === 1 ? "didėjančiai" : "mažėjančiai";
    return html`<th aria-sort=${active ? (this.sortDir === 1 ? "ascending" : "descending") : "none"}>
      <button
        class="sort"
        title=${active ? `Rūšiuojama ${dirWord}` : "Rūšiuoti"}
        @click=${() => this.onSort(col.key)}
      >
        ${col.label}${active ? html`<span class="arrow"> ${this.sortDir === 1 ? "▲" : "▼"}</span>` : nothing}
      </button>
    </th>`;
  }

  private renderRow(row: Row, columns: Column[]) {
    return html`<tr
      class=${row.id === this.selectedId ? "selected" : ""}
      tabindex="0"
      aria-label=${`Artefaktas ${row.name}`}
      @click=${() => this.emitOpen(row)}
      @keydown=${(e: KeyboardEvent) => this.onRowKeydown(e, row)}
    >
      ${columns.map((col) => this.renderCell(row, col))}
      <td class="actions">
        ${this.actions.map(
          (action) => html`<button
            class="linklike"
            title=${action.rowTitle}
            @click=${(e: Event) => {
              e.stopPropagation(); // the row click would also open the panel
              this.emitAction(action.id, row);
            }}
          >
            ${action.label}
          </button>`,
        )}
        ${this.lockActions(row).map(
          (action) => html`<button
            class="linklike"
            title=${action.title}
            @click=${(e: Event) => {
              e.stopPropagation();
              this.emitLockAction(action.id, row);
            }}
          >
            ${action.label}
          </button>`,
        )}
      </td>
    </tr>`;
  }

  /** The lock buttons for one row — pure capability data + row state
   * (ui/src/lib/locking.ts), never a host branch. Empty when the shell
   * cannot lock, so the download-only UX stays byte-identical there. */
  private lockActions(row: Row) {
    if (!this.lockUi) return [];
    return lockActionsFor(lockStateOf(row, this.me), this.canReturn);
  }

  private renderCell(row: Row, col: Column) {
    const value = (row as unknown as Record<string, unknown>)[col.key];
    switch (col.key) {
      case "currentVersionNumber":
        // Append-only today: current pointer is always the newest version,
        // so the count equals the current number and "v2 (2)" would be
        // redundant. The count is shown only when the two diverge (a future
        // current-pointer repoint), as "v2 (5 versijos)".
        return html`<td>
          ${
            row.currentVersionNumber === null
              ? ""
              : row.currentVersionNumber === row.versionCount
                ? `v${row.currentVersionNumber}`
                : `v${row.currentVersionNumber} (${row.versionCount} versijos)`
          }
        </td>`;
      case "lockedBy": {
        // Free rows render an EMPTY cell — a badge saying "nobody has this"
        // on every free file would be noise (spec §8: the badge exists for
        // the two taken states).
        const badge = lockBadge(row, this.me, Date.now());
        return html`<td>
          ${badge === null
            ? ""
            : html`<span class=${`lock-badge ${badge.state}`} title=${badge.title}
                >${badge.text}</span
              >`}
        </td>`;
      }
      case "sizeBytes":
        return html`<td>${formatSize(row.sizeBytes)}</td>`;
      case "updatedAt":
        return html`<td>${formatDate(row.updatedAt)}</td>`;
      case "name":
      case "currentVersionMessage":
        return html`<td class="wrap">${value ?? ""}</td>`;
      default:
        return html`<td>${value ?? ""}</td>`;
    }
  }

  private onRowKeydown(e: KeyboardEvent, row: Row) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.emitOpen(row);
    }
  }

  private onSort(key: string) {
    const next = nextSort({ key: this.sortKey, dir: this.sortDir }, key);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  private emitOpen(row: Row) {
    this.dispatchEvent(
      new CustomEvent("open-artifact", { detail: { row }, bubbles: true, composed: true }),
    );
  }

  /** Emit the typed intent for `action` on this row's CURRENT version
   * (omitted versionNumber = current — the adapter resolves it). */
  private emitAction(action: ArtifactActionId, row: Row) {
    this.dispatchEvent(
      new CustomEvent<ArtifactActionIntent>("artifact-action", {
        detail: artifactActionIntent(action, row.id),
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Emit a take-for-work intent; the parent owns every lock call (and the
   * note prompt / takeover confirm the intent may need first). */
  private emitLockAction(action: LockActionId, row: Row) {
    this.dispatchEvent(
      new CustomEvent<LockActionEvent>("lock-action", {
        detail: { action, artifactId: row.id },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("modulina-artifact-table", ModulinaArtifactTable);
